import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { WO_STATUS, eur } from "@/lib/constants";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  Plus,
  Trash2,
  Loader2,
  ClipboardList,
  FileSignature,
  Download,
  Camera,
  X,
  MapPin,
  Search,
  SlidersHorizontal,
  Clock,
  CheckCircle2,
  Euro,
  UserCheck,
  Pencil,
  Package,
  PackagePlus,
  Minus,
} from "lucide-react";

import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import { fileToScaledDataUrl } from "@/lib/image";

const EMPTY_FORM = {
  title: "",
  description: "",
  client_id: "",
  asset_id: "",
  technician_id: "",
  scheduled_date: "",
  status: "pendiente",
  amount: 0,
  hours_worked: 0,
  materials: "",
  inventory_items: [],
};

const ALL_FILTER = "todos";

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon
          className="h-5 w-5"
          style={{ color }}
          strokeWidth={1.8}
        />
      </div>

      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>

        <p className="text-sm text-[#86868B]">
          {label}
        </p>
      </div>
    </div>
  );
}

export default function WorkOrders() {
  const [workOrders, setWorkOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [assets, setAssets] = useState([]);

  const [open, setOpen] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL_FILTER);
  const [technicianFilter, setTechnicianFilter] = useState(ALL_FILTER);

  const [signWorkOrder, setSignWorkOrder] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState("Cliente");
  const [gps, setGps] = useState(null);
  const [signing, setSigning] = useState(false);

  const [photoWorkOrder, setPhotoWorkOrder] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);

  const loadWorkOrders = async () => {
    try {
      const response = await api.get("/work-orders");
      setWorkOrders(response.data || []);
    } catch (error) {
      toast.error(
        formatApiError(error) ||
          "No se pudieron cargar las órdenes de trabajo."
      );
    }
  };

  const loadInitialData = async () => {
    setLoading(true);

    try {
      const [
        workOrdersResponse,
        clientsResponse,
        techniciansResponse,
        inventoryResponse,
        assetsResponse,
      ] = await Promise.all([
        api.get("/work-orders"),
        api.get("/clients"),
        api.get("/technicians"),
        api.get("/inventory/products"),
        api.get("/assets"),
      ]);

      setWorkOrders(workOrdersResponse.data || []);
      setClients(clientsResponse.data || []);
      setTechnicians(techniciansResponse.data || []);
      setInventoryProducts(inventoryResponse.data || []);
      setAssets(assetsResponse.data || []);
    } catch (error) {
      toast.error(
        formatApiError(error) ||
          "No se pudieron cargar los datos."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const resetOrderForm = () => {
    setEditingWorkOrder(null);
    setForm(EMPTY_FORM);
  };

  const openCreateOrder = () => {
    resetOrderForm();
    setOpen(true);
  };

  const openEditOrder = (workOrder) => {
    if (workOrder.locked) {
      toast.error("Esta orden está firmada y no puede editarse.");
      return;
    }

    setEditingWorkOrder(workOrder);
    setForm({
      title: workOrder.title || "",
      description: workOrder.description || "",
      client_id: workOrder.client_id || "",
      asset_id: workOrder.asset_id || "",
      technician_id: workOrder.technician_id || "",
      scheduled_date: workOrder.scheduled_date
        ? String(workOrder.scheduled_date).slice(0, 10)
        : "",
      status: workOrder.status || "pendiente",
      amount: workOrder.amount ?? 0,
      hours_worked: workOrder.hours_worked ?? 0,
      materials: workOrder.materials || "",
      inventory_items:
        workOrder.inventory_items?.map((item) => ({
          product_id: item.product_id || "",
          quantity: item.quantity ?? 1,
        })) || [],
    });
    setOpen(true);
  };

  const addInventoryItem = () => {
    setForm((current) => ({
      ...current,
      inventory_items: [
        ...current.inventory_items,
        { product_id: "", quantity: 1 },
      ],
    }));
  };

  const updateInventoryItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      inventory_items: current.inventory_items.map(
        (item, itemIndex) =>
          itemIndex === index
            ? { ...item, [key]: value }
            : item
      ),
    }));
  };

  const removeInventoryItem = (index) => {
    setForm((current) => ({
      ...current,
      inventory_items: current.inventory_items.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const selectedInventoryTotals = useMemo(() => {
    return form.inventory_items.reduce(
      (totals, item) => {
        const product = inventoryProducts.find(
          (candidate) => candidate.id === item.product_id
        );
        const quantity = Number(item.quantity) || 0;

        if (!product) return totals;

        totals.cost +=
          quantity * Number(product.purchase_price || 0);
        totals.sale +=
          quantity * Number(product.sale_price || 0);
        return totals;
      },
      { cost: 0, sale: 0 }
    );
  }, [form.inventory_items, inventoryProducts]);

  const save = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("Escribe el título de la orden.");
      return;
    }

    if (!form.client_id) {
      toast.error("Selecciona un cliente.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        materials: form.materials.trim(),
        inventory_items: form.inventory_items
          .filter(
            (item) =>
              item.product_id &&
              Number(item.quantity) > 0
          )
          .map((item) => ({
            product_id: item.product_id,
            quantity: Number(item.quantity),
          })),
        technician_id: form.technician_id || null,
        scheduled_date: form.scheduled_date || null,
        amount: Number.parseFloat(form.amount) || 0,
        hours_worked: Number.parseFloat(form.hours_worked) || 0,
      };

      if (editingWorkOrder) {
        await api.put(
          "/work-orders/" + editingWorkOrder.id,
          payload
        );
        toast.success("Orden de trabajo actualizada.");
      } else {
        await api.post("/work-orders", payload);
        toast.success("Orden de trabajo creada.");
      }

      setOpen(false);
      resetOrderForm();
      await loadWorkOrders();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (workOrder, status) => {
    if (workOrder.locked) {
      toast.error("Esta orden está firmada y bloqueada.");
      return;
    }

    setUpdatingId(workOrder.id);

    try {
      await api.put(`/work-orders/${workOrder.id}`, {
        status,
      });

      setWorkOrders((current) =>
        current.map((item) =>
          item.id === workOrder.id
            ? { ...item, status }
            : item
        )
      );

      toast.success("Estado actualizado.");
    } catch (error) {
      toast.error(formatApiError(error));
      await loadWorkOrders();
    } finally {
      setUpdatingId(null);
    }
  };

  const remove = async (workOrder) => {
    if (workOrder.locked) {
      toast.error("No puedes eliminar una orden firmada.");
      return;
    }

    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar la orden “${workOrder.title}”?`
    );

    if (!confirmed) return;

    setDeletingId(workOrder.id);

    try {
      await api.delete(`/work-orders/${workOrder.id}`);

      setWorkOrders((current) =>
        current.filter((item) => item.id !== workOrder.id)
      );

      toast.success("Orden eliminada.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const downloadPdf = async (workOrder) => {
    try {
      const response = await api.get(
        `/work-orders/${workOrder.id}/pdf`,
        { responseType: "blob" }
      );

      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60_000);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openSign = (workOrder) => {
    if (workOrder.signed) {
      toast.info("Este parte ya está firmado.");
      return;
    }

    setSignWorkOrder(workOrder);
    setSignerName(workOrder.client_name || "Cliente");
    setSignature(null);
    setGps(null);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          setGps(
            `${position.coords.latitude.toFixed(5)},${position.coords.longitude.toFixed(5)}`
          ),
        () => setGps(null),
        { timeout: 5000 }
      );
    }
  };

  const saveSignature = async () => {
    if (!signature) {
      toast.error("La firma es obligatoria.");
      return;
    }

    setSigning(true);

    try {
      await api.post(
        `/work-orders/${signWorkOrder.id}/sign`,
        {
          signature,
          signed_by: signerName.trim() || "Cliente",
          gps,
        }
      );

      toast.success("Parte firmado y completado.");

      setSignWorkOrder(null);
      setSignature(null);
      setSignerName("Cliente");
      setGps(null);

      await loadWorkOrders();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSigning(false);
    }
  };

  const openPhotos = async (workOrder) => {
    setPhotoWorkOrder(workOrder);
    setPhotos([]);
    setPhotoBusy(true);

    try {
      const { data } = await api.get(
        `/work-orders/${workOrder.id}`
      );

      setPhotos(data.photos || []);
    } catch (error) {
      toast.error(formatApiError(error));
      setPhotoWorkOrder(null);
    } finally {
      setPhotoBusy(false);
    }
  };

  const addPhotos = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    const remainingSlots = 6 - photos.length;
    const selectedFiles = files.slice(0, remainingSlots);

    setPhotoBusy(true);

    try {
      const scaledPhotos = await Promise.all(
        selectedFiles.map((file) =>
          fileToScaledDataUrl(file)
        )
      );

      setPhotos((current) =>
        [...current, ...scaledPhotos].slice(0, 6)
      );
    } catch {
      toast.error("No se pudieron procesar las fotografías.");
    } finally {
      setPhotoBusy(false);
      event.target.value = "";
    }
  };

  const savePhotos = async () => {
    if (!photoWorkOrder) return;

    setPhotoBusy(true);

    try {
      await api.put(
        `/work-orders/${photoWorkOrder.id}`,
        { photos }
      );

      toast.success("Fotografías guardadas.");

      setPhotoWorkOrder(null);
      setPhotos([]);

      await loadWorkOrders();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPhotoBusy(false);
    }
  };

  const filteredWorkOrders = useMemo(() => {
    const normalizedQuery = query
      .trim()
      .toLowerCase();

    return workOrders.filter((workOrder) => {
      const matchesQuery =
        !normalizedQuery ||
        [
          workOrder.number,
          workOrder.title,
          workOrder.description,
          workOrder.client_name,
          workOrder.technician_name,
          workOrder.asset_name,
          workOrder.asset_serial_number,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);

      const matchesStatus =
        statusFilter === ALL_FILTER ||
        workOrder.status === statusFilter;

      const matchesTechnician =
        technicianFilter === ALL_FILTER ||
        (technicianFilter === "sin_asignar"
          ? !workOrder.technician_id
          : workOrder.technician_id === technicianFilter);

      return (
        matchesQuery &&
        matchesStatus &&
        matchesTechnician
      );
    });
  }, [
    workOrders,
    query,
    statusFilter,
    technicianFilter,
  ]);

  const totalAmount = workOrders.reduce(
    (total, workOrder) =>
      total + Number(workOrder.amount || 0),
    0
  );

  const pendingCount = workOrders.filter(
    (workOrder) => workOrder.status === "pendiente"
  ).length;

  const inProgressCount = workOrders.filter(
    (workOrder) => workOrder.status === "en_curso"
  ).length;

  const completedCount = workOrders.filter(
    (workOrder) => workOrder.status === "completada"
  ).length;

  const assignedCount = workOrders.filter(
    (workOrder) => Boolean(workOrder.technician_id)
  ).length;

  const filtersActive =
    Boolean(query.trim()) ||
    statusFilter !== ALL_FILTER ||
    technicianFilter !== ALL_FILTER;

  const clearFilters = () => {
    setQuery("");
    setStatusFilter(ALL_FILTER);
    setTechnicianFilter(ALL_FILTER);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Órdenes de trabajo"
        subtitle={`${workOrders.length} órdenes · ${eur(totalAmount)} en total`}
        action={
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);

              if (!nextOpen) {
                resetOrderForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="new-wo-btn"
                type="button"
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nueva orden
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                  {editingWorkOrder
                    ? "Editar orden de trabajo"
                    : "Nueva orden de trabajo"}
                </DialogTitle>

                <p className="mt-1 text-sm text-[#86868B]">
                  {editingWorkOrder
                    ? "Modifica los datos de la orden y guarda los cambios."
                    : "Planifica el trabajo, asigna un técnico y registra el importe."}
                </p>
              </DialogHeader>

              <form
                onSubmit={save}
                className="mt-4 space-y-5"
              >
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Título
                  </label>

                  <input
                    data-testid="wo-title"
                    value={form.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        title: event.target.value,
                      })
                    }
                    required
                    autoFocus
                    className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    placeholder="Ej. Reparación de bomba de agua"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Cliente
                    </label>

                    <select
                      data-testid="wo-client"
                      value={form.client_id}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          client_id: event.target.value,
                          asset_id: "",
                        })
                      }
                      required
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      <option value="">
                        Selecciona un cliente
                      </option>

                      {clients.map((client) => (
                        <option
                          key={client.id}
                          value={client.id}
                        >
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Técnico
                    </label>

                    <select
                      data-testid="wo-tech"
                      value={form.technician_id}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          technician_id: event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      <option value="">
                        Sin asignar
                      </option>

                      {technicians.map((technician) => (
                        <option
                          key={technician.id}
                          value={technician.id}
                        >
                          {technician.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Equipo / activo del cliente
                  </label>

                  <select
                    value={form.asset_id}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        asset_id: event.target.value,
                      })
                    }
                    disabled={!form.client_id}
                    className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20 disabled:opacity-50"
                  >
                    <option value="">
                      {form.client_id
                        ? "Sin equipo específico"
                        : "Selecciona primero un cliente"}
                    </option>

                    {assets
                      .filter(
                        (asset) =>
                          asset.client_id === form.client_id &&
                          asset.status !== "baja"
                      )
                      .map((asset) => (
                        <option
                          key={asset.id}
                          value={asset.id}
                        >
                          {asset.name}
                          {asset.serial_number
                            ? ` · ${asset.serial_number}`
                            : ""}
                          {asset.location
                            ? ` · ${asset.location}`
                            : ""}
                        </option>
                      ))}
                  </select>

                  {form.client_id &&
                    !assets.some(
                      (asset) =>
                        asset.client_id === form.client_id &&
                        asset.status !== "baja"
                    ) && (
                      <p className="mt-2 text-xs text-[#86868B]">
                        Este cliente todavía no tiene equipos registrados.
                      </p>
                    )}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Descripción
                  </label>

                  <textarea
                    data-testid="wo-description"
                    value={form.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        description: event.target.value,
                      })
                    }
                    rows={4}
                    className="w-full resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    placeholder="Describe el trabajo que debe realizarse."
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Materiales y observaciones
                  </label>

                  <textarea
                    data-testid="wo-materials"
                    value={form.materials}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        materials: event.target.value,
                      })
                    }
                    rows={3}
                    className="w-full resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    placeholder="Material previsto, referencias o notas."
                  />
                </div>

                <div className="rounded-2xl border border-[#E8E8ED] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-2 font-medium text-[#1D1D1F]">
                        <Package className="h-4 w-4 text-[#0066CC]" />
                        Materiales del inventario
                      </p>
                      <p className="mt-1 text-xs text-[#86868B]">
                        Al guardar se descontarán automáticamente del stock.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addInventoryItem}
                      disabled={inventoryProducts.length === 0}
                      className="flex items-center gap-1.5 rounded-full bg-[#0066CC]/10 px-3 py-2 text-xs font-medium text-[#0066CC] disabled:opacity-50"
                    >
                      <PackagePlus className="h-3.5 w-3.5" />
                      Añadir material
                    </button>
                  </div>

                  {inventoryProducts.length === 0 ? (
                    <p className="mt-4 rounded-xl bg-[#FFF4E5] p-3 text-sm text-[#C93400]">
                      No hay productos en Inventario. Crea primero los materiales.
                    </p>
                  ) : form.inventory_items.length === 0 ? (
                    <p className="mt-4 rounded-xl bg-[#F5F5F7] p-4 text-center text-sm text-[#86868B]">
                      Esta orden todavía no tiene materiales del almacén.
                    </p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {form.inventory_items.map((item, index) => {
                        const selectedProduct =
                          inventoryProducts.find(
                            (product) =>
                              product.id === item.product_id
                          );

                        return (
                          <div
                            key={index}
                            className="grid grid-cols-1 gap-3 rounded-xl bg-[#F5F5F7] p-3 sm:grid-cols-[minmax(0,1fr)_120px_40px]"
                          >
                            <div>
                              <label className="mb-1 block text-xs font-medium text-[#86868B]">
                                Producto
                              </label>
                              <select
                                value={item.product_id}
                                onChange={(event) =>
                                  updateInventoryItem(
                                    index,
                                    "product_id",
                                    event.target.value
                                  )
                                }
                                required
                                className="w-full rounded-lg bg-white px-3 py-2.5 text-sm outline-none"
                              >
                                <option value="">
                                  Selecciona un producto
                                </option>
                                {inventoryProducts.map((product) => (
                                  <option
                                    key={product.id}
                                    value={product.id}
                                  >
                                    {product.sku} · {product.name} · stock {product.stock} {product.unit}
                                  </option>
                                ))}
                              </select>

                              {selectedProduct && (
                                <p className="mt-1 text-xs text-[#86868B]">
                                  Coste {eur(selectedProduct.purchase_price)} · Venta {eur(selectedProduct.sale_price)}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="mb-1 block text-xs font-medium text-[#86868B]">
                                Cantidad
                              </label>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={item.quantity}
                                onChange={(event) =>
                                  updateInventoryItem(
                                    index,
                                    "quantity",
                                    event.target.value
                                  )
                                }
                                required
                                className="w-full rounded-lg bg-white px-3 py-2.5 text-sm outline-none"
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                removeInventoryItem(index)
                              }
                              title="Quitar material"
                              className="mt-5 flex h-10 w-10 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {form.inventory_items.length > 0 && (
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="rounded-xl bg-[#F5F5F7] p-3">
                        <p className="text-xs text-[#86868B]">
                          Coste materiales
                        </p>
                        <p className="mt-1 font-mono font-semibold">
                          {eur(selectedInventoryTotals.cost)}
                        </p>
                      </div>
                      <div className="rounded-xl bg-[#F5F5F7] p-3">
                        <p className="text-xs text-[#86868B]">
                          Precio de venta
                        </p>
                        <p className="mt-1 font-mono font-semibold">
                          {eur(selectedInventoryTotals.sale)}
                        </p>
                      </div>
                      <div className="rounded-xl bg-[#E9F9EE] p-3">
                        <p className="text-xs text-[#248A3D]">
                          Margen materiales
                        </p>
                        <p className="mt-1 font-mono font-semibold text-[#248A3D]">
                          {eur(
                            selectedInventoryTotals.sale -
                              selectedInventoryTotals.cost
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Importe (€)
                    </label>

                    <input
                      data-testid="wo-amount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          amount: event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Horas
                    </label>

                    <input
                      data-testid="wo-hours"
                      type="number"
                      min="0"
                      step="0.25"
                      value={form.hours_worked}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          hours_worked: event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Fecha
                    </label>

                    <input
                      data-testid="wo-date"
                      type="date"
                      value={form.scheduled_date}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          scheduled_date: event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium text-[#1D1D1F] transition-colors hover:bg-[#F5F5F7]"
                  >
                    Cancelar
                  </button>

                  <button
                    data-testid="save-wo"
                    type="submit"
                    disabled={saving || clients.length === 0}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.25)] transition-all hover:bg-[#004499] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}

                    {saving
                      ? "Guardando..."
                      : editingWorkOrder
                        ? "Guardar cambios"
                        : "Crear orden"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={Clock}
          label="Pendientes"
          value={pendingCount}
          color="#86868B"
        />

        <SummaryCard
          icon={ClipboardList}
          label="En curso"
          value={inProgressCount}
          color="#FF9500"
        />

        <SummaryCard
          icon={CheckCircle2}
          label="Completadas"
          value={completedCount}
          color="#34C759"
        />

        <SummaryCard
          icon={Euro}
          label="Importe total"
          value={eur(totalAmount)}
          color="#0066CC"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

            <input
              value={query}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-10 text-sm text-[#1D1D1F] outline-none transition-all focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
              placeholder="Buscar por número, trabajo, cliente o técnico..."
            />

            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-white hover:text-[#1D1D1F]"
                aria-label="Borrar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <SlidersHorizontal className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

              <select
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value)
                }
                className="w-full min-w-44 appearance-none rounded-xl bg-[#F5F5F7] py-3 pl-10 pr-8 text-sm text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#0066CC]/20 sm:w-auto"
              >
                <option value={ALL_FILTER}>
                  Todos los estados
                </option>

                {WO_STATUS.map((status) => (
                  <option
                    key={status.value}
                    value={status.value}
                  >
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <select
              value={technicianFilter}
              onChange={(event) =>
                setTechnicianFilter(event.target.value)
              }
              className="w-full min-w-44 rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#0066CC]/20 sm:w-auto"
            >
              <option value={ALL_FILTER}>
                Todos los técnicos
              </option>

              <option value="sin_asignar">
                Sin asignar
              </option>

              {technicians.map((technician) => (
                <option
                  key={technician.id}
                  value={technician.id}
                >
                  {technician.name}
                </option>
              ))}
            </select>

            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-xl px-4 py-3 text-sm font-medium text-[#0066CC] transition-colors hover:bg-[#0066CC]/10"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#86868B]">
          <span>
            Mostrando {filteredWorkOrders.length} de {workOrders.length} órdenes
          </span>

          <span>
            {assignedCount} con técnico asignado
          </span>
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />

            <p className="mt-3 text-sm text-[#86868B]">
              Cargando órdenes...
            </p>
          </div>
        </div>
      ) : workOrders.length === 0 ? (
        <div className="mantix-card py-8">
          <EmptyState
            title="No hay órdenes de trabajo"
            subtitle="Crea una orden para planificar el trabajo, registrar materiales, añadir fotografías y obtener la firma del cliente."
          />

          <div className="-mt-4 flex justify-center pb-6">
            <button
              type="button"
              onClick={openCreateOrder}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.25)] hover:bg-[#004499]"
            >
              <Plus className="h-4 w-4" />
              Crear primera orden
            </button>
          </div>
        </div>
      ) : filteredWorkOrders.length === 0 ? (
        <div className="mantix-card flex min-h-72 flex-col items-center justify-center p-8 text-center">
          <Search className="h-9 w-9 text-[#B0B0B5]" />

          <h3 className="mt-4 font-display text-lg font-semibold text-[#1D1D1F]">
            No hay resultados
          </h3>

          <p className="mt-1 text-sm text-[#86868B]">
            Prueba a cambiar la búsqueda o los filtros.
          </p>

          <button
            type="button"
            onClick={clearFilters}
            className="mt-5 rounded-full bg-[#0066CC]/10 px-5 py-2.5 text-sm font-medium text-[#0066CC] hover:bg-[#0066CC]/15"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-[#D2D2D7]/50 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#D2D2D7]/50 text-xs uppercase tracking-wider text-[#86868B]">
                  <th className="px-6 py-4 font-semibold">
                    Trabajo
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Cliente
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Equipo
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Técnico
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Fecha
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Importe
                  </th>
                  <th className="px-6 py-4 font-semibold">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-right font-semibold">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredWorkOrders.map((workOrder) => {
                  const isUpdating =
                    updatingId === workOrder.id;

                  const isDeleting =
                    deletingId === workOrder.id;

                  return (
                    <tr
                      key={workOrder.id}
                      data-testid={`wo-row-${workOrder.id}`}
                      className="border-b border-[#D2D2D7]/30 transition-colors last:border-b-0 hover:bg-[#F5F5F7]/50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#0066CC]/10">
                            <ClipboardList className="h-4 w-4 text-[#0066CC]" />
                          </div>

                          <div className="min-w-0">
                            <span className="block max-w-[250px] truncate font-medium text-[#1D1D1F]">
                              {workOrder.title}
                            </span>

                            {workOrder.number && (
                              <span className="font-mono text-xs text-[#86868B]">
                                {workOrder.number}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-[#86868B]">
                        {workOrder.client_name || "—"}
                      </td>

                      <td className="px-6 py-4 text-[#86868B]">
                        {workOrder.technician_name || "Sin asignar"}
                      </td>

                      <td className="px-6 py-4 font-mono text-xs text-[#86868B]">
                        {workOrder.scheduled_date || "—"}
                      </td>

                      <td className="px-6 py-4 font-mono font-medium text-[#1D1D1F]">
                        {eur(workOrder.amount || 0)}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <StatusBadge
                            list={WO_STATUS}
                            value={workOrder.status}
                          />

                          <select
                            data-testid={`wo-status-${workOrder.id}`}
                            value={workOrder.status}
                            onChange={(event) =>
                              changeStatus(
                                workOrder,
                                event.target.value
                              )
                            }
                            disabled={
                              workOrder.locked ||
                              isUpdating ||
                              isDeleting
                            }
                            className="rounded-lg bg-[#F5F5F7] px-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0066CC]/20 disabled:opacity-60"
                          >
                            {WO_STATUS.map((status) => (
                              <option
                                key={status.value}
                                value={status.value}
                              >
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            data-testid={`wo-edit-${workOrder.id}`}
                            type="button"
                            onClick={() => openEditOrder(workOrder)}
                            disabled={workOrder.locked || isUpdating || isDeleting}
                            title={
                              workOrder.locked
                                ? "La orden está firmada y bloqueada"
                                : "Editar orden"
                            }
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] transition-all hover:bg-[#EAF2FC] hover:text-[#0066CC] disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>

                          <button
                            data-testid={`wo-photos-${workOrder.id}`}
                            type="button"
                            onClick={() =>
                              openPhotos(workOrder)
                            }
                            title="Fotografías"
                            className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-all ${
                              workOrder.photos_count > 0
                                ? "bg-[#EAF2FC] text-[#0066CC]"
                                : "text-[#86868B] hover:bg-[#F5F5F7]"
                            }`}
                          >
                            <Camera className="h-4 w-4" />

                            {workOrder.photos_count > 0 && (
                              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#0066CC] px-1 text-[10px] font-semibold text-white">
                                {workOrder.photos_count}
                              </span>
                            )}
                          </button>

                          <button
                            data-testid={`wo-sign-${workOrder.id}`}
                            type="button"
                            onClick={() =>
                              openSign(workOrder)
                            }
                            disabled={workOrder.signed}
                            title={
                              workOrder.signed
                                ? "Parte firmado"
                                : "Firmar parte"
                            }
                            className={`flex h-9 w-9 items-center justify-center rounded-lg transition-all ${
                              workOrder.signed
                                ? "bg-[#E9F9EE] text-[#34C759]"
                                : "text-[#86868B] hover:bg-[#F5F5F7]"
                            } disabled:cursor-not-allowed`}
                          >
                            <FileSignature className="h-4 w-4" />
                          </button>

                          <button
                            data-testid={`wo-pdf-${workOrder.id}`}
                            type="button"
                            onClick={() =>
                              downloadPdf(workOrder)
                            }
                            title="Abrir parte PDF"
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] transition-all hover:bg-[#F5F5F7] hover:text-[#0066CC]"
                          >
                            <Download className="h-4 w-4" />
                          </button>

                          <button
                            data-testid={`wo-delete-${workOrder.id}`}
                            type="button"
                            onClick={() =>
                              remove(workOrder)
                            }
                            disabled={
                              workOrder.locked ||
                              isUpdating ||
                              isDeleting
                            }
                            title={
                              workOrder.locked
                                ? "La orden está firmada y bloqueada"
                                : "Eliminar orden"
                            }
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#D2D2D7] transition-all hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog
        open={Boolean(signWorkOrder)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSignWorkOrder(null);
            setSignature(null);
            setSignerName("Cliente");
            setGps(null);
          }
        }}
      >
        <DialogContent
          className="max-w-md rounded-3xl border-none bg-white p-6 shadow-2xl"
          data-testid="sign-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-[#1D1D1F]">
              Firmar parte de trabajo
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-2xl bg-[#F5F5F7] p-4 text-sm">
              <p className="font-medium text-[#1D1D1F]">
                {signWorkOrder?.title}
              </p>

              <p className="mt-1 text-[#86868B]">
                {signWorkOrder?.client_name}
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                Nombre de quien firma
              </label>

              <input
                data-testid="signer-name"
                value={signerName}
                onChange={(event) =>
                  setSignerName(event.target.value)
                }
                className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none focus:ring-2 focus:ring-[#0066CC]/20"
              />
            </div>

            <SignaturePad onChange={setSignature} />

            <div className="flex items-center gap-2 text-xs text-[#86868B]">
              <MapPin
                className="h-3.5 w-3.5"
                data-testid="gps-indicator"
              />

              {gps ? (
                <span data-testid="gps-value">
                  Ubicación capturada: {gps}
                </span>
              ) : (
                <span>
                  Ubicación GPS no disponible (opcional)
                </span>
              )}
            </div>

            <button
              data-testid="save-signature"
              type="button"
              onClick={saveSignature}
              disabled={signing}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white transition-all hover:bg-[#004499] disabled:opacity-60"
            >
              {signing && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}

              {signing
                ? "Guardando firma..."
                : "Guardar firma y completar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(photoWorkOrder)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPhotoWorkOrder(null);
            setPhotos([]);
          }
        }}
      >
        <DialogContent
          className="max-w-lg rounded-3xl border-none bg-white p-6 shadow-2xl"
          data-testid="photo-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-[#1D1D1F]">
              Fotografías del trabajo
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-[#86868B]">
              {photoWorkOrder?.title} · máximo 6 fotografías. Se incluirán en el PDF del parte.
            </p>

            {photoBusy && photos.length === 0 ? (
              <div className="flex h-40 items-center justify-center rounded-2xl bg-[#F5F5F7]">
                <Loader2 className="h-6 w-6 animate-spin text-[#0066CC]" />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {photos.map((photo, index) => (
                  <div
                    key={`${photo.slice(0, 30)}-${index}`}
                    className="group relative aspect-square overflow-hidden rounded-xl bg-[#F5F5F7]"
                  >
                    <img
                      src={photo}
                      alt={`Fotografía ${index + 1}`}
                      className="h-full w-full object-cover"
                    />

                    {!photoWorkOrder?.locked && (
                      <button
                        data-testid={`remove-photo-${index}`}
                        type="button"
                        onClick={() =>
                          setPhotos((current) =>
                            current.filter(
                              (_, currentIndex) =>
                                currentIndex !== index
                            )
                          )
                        }
                        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/65 text-white opacity-0 transition-opacity group-hover:opacity-100"
                        aria-label={`Eliminar fotografía ${index + 1}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}

                {!photoWorkOrder?.locked &&
                  photos.length < 6 && (
                    <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#D2D2D7] text-[#86868B] transition-colors hover:border-[#0066CC] hover:text-[#0066CC]">
                      {photoBusy ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Camera className="h-5 w-5" />
                      )}

                      <span className="text-xs">
                        Añadir
                      </span>

                      <input
                        data-testid="photo-input"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={addPhotos}
                        className="hidden"
                      />
                    </label>
                  )}
              </div>
            )}

            {photoWorkOrder?.locked ? (
              <p className="rounded-xl bg-[#F5F5F7] p-3 text-center text-sm text-[#86868B]">
                Parte firmado y bloqueado. Las fotografías están en modo solo lectura.
              </p>
            ) : (
              <button
                data-testid="save-photos"
                type="button"
                onClick={savePhotos}
                disabled={photoBusy}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white transition-all hover:bg-[#004499] disabled:opacity-60"
              >
                {photoBusy && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}

                {photoBusy
                  ? "Guardando..."
                  : "Guardar fotografías"}
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}